import { useQrMatrix } from '@/application/usecases/useIntake';

/**
 * The printed tray code, rendered client side from `qr.value`. Section 7.7.
 *
 * The value encoded is the bare code string, never a URL. A URL is longer, so
 * it needs a denser symbol that degrades faster on a toner starved print, and
 * it puts the host name on every tray in the building. Section 11.3.
 *
 * Drawn as one path of rectangles rather than a grid of elements, because a
 * 45 module symbol is two thousand DOM nodes otherwise and the print pipeline
 * feels it.
 */

/**
 * The quiet zone, in modules, drawn inside the viewBox.
 *
 * A QR symbol is unreadable without four clear modules on every side: that
 * blank border is what the detector measures the finder patterns against.
 * The encoder emits the matrix bare and this used to be left to the sheet's
 * CSS, which does not provide it. At 56mm a module is about 2.7mm, so four of
 * them is nearly 11mm, and the label allows 7mm above and 6mm below with the
 * lab name and the job order number printed into that space. Trays came off
 * the printer looking correct and would not scan.
 *
 * Drawing it here rather than in CSS means it scales with the symbol and no
 * later layout change can squeeze it.
 */
const QUIET_MODULES = 4;

export function QrCode({ value }: { value: string }) {
  const { data, isPending, isError } = useQrMatrix(value);

  if (isPending || isError || data === undefined) {
    // A blank square keeps the label geometry stable while the encoder loads,
    // so the sheet never reflows between paint and print.
    return <svg viewBox="0 0 1 1" role="img" aria-label={value} />;
  }

  const side = data.size + QUIET_MODULES * 2;

  const path: string[] = [];
  for (let y = 0; y < data.size; y += 1) {
    for (let x = 0; x < data.size; x += 1) {
      if (data.modules[y * data.size + x] === true) {
        path.push(`M${x + QUIET_MODULES} ${y + QUIET_MODULES}h1v1h-1z`);
      }
    }
  }

  return (
    <svg viewBox={`0 0 ${side} ${side}`} role="img" aria-label={value}>
      <rect width={side} height={side} fill="#fff" />
      <path d={path.join('')} fill="#15130f" />
    </svg>
  );
}
